import { useEffect, useMemo, useState } from 'react'
import { Loading, Confirm, Notify } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'

interface EngagementUser {
    id: number
    name: string
    email: string
    status: string
    organisation: string
    admin: number | boolean
    team_id: number | null
    team_name: string | null
    is_leader?: boolean
    cognito_id?: string
    created_at?: string
}

interface PcpReferralTeam {
    id: number
    name: string
    description: string | null
    member_count?: number
    created_at?: string
}

type TabKey = 'users' | 'teams'

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const LOWER = 'abcdefghijklmnopqrstuvwxyz'
const NUMS = '0123456789'
const SPECIAL = '!@#$%^&*'

const generateTempPassword = (): string => {
    let pwd = ''
    pwd += UPPER[Math.floor(Math.random() * UPPER.length)]
    pwd += LOWER[Math.floor(Math.random() * LOWER.length)]
    pwd += NUMS[Math.floor(Math.random() * NUMS.length)]
    pwd += SPECIAL[Math.floor(Math.random() * SPECIAL.length)]
    const all = UPPER + LOWER + NUMS + SPECIAL
    for (let i = pwd.length; i < 12; i++) {
        pwd += all[Math.floor(Math.random() * all.length)]
    }
    return pwd
        .split('')
        .sort(() => Math.random() - 0.5)
        .join('')
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// A single parsed CSV row destined for creation.
interface BulkRow {
    email: string
    name: string
    team: string
}

// The outcome for one row after the creation loop runs.
interface BulkResultRow extends BulkRow {
    status: 'created' | 'failed'
    password?: string
    reason?: string
}

// Minimal CSV parser: supports quoted fields (with embedded commas, quotes and
// newlines) and a header row. Returns an array of objects keyed by the
// lower-cased header names. No external dependency needed.
const parseCsv = (text: string): Record<string, string>[] => {
    const rows: string[][] = []
    let field = ''
    let row: string[] = []
    let inQuotes = false

    // Strip a leading BOM if present so the first header isn't corrupted.
    const input = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text

    for (let i = 0; i < input.length; i++) {
        const char = input[i]
        if (inQuotes) {
            if (char === '"') {
                if (input[i + 1] === '"') {
                    field += '"'
                    i++
                } else {
                    inQuotes = false
                }
            } else {
                field += char
            }
        } else if (char === '"') {
            inQuotes = true
        } else if (char === ',') {
            row.push(field)
            field = ''
        } else if (char === '\n' || char === '\r') {
            // Handle CRLF by skipping the paired \n after a \r.
            if (char === '\r' && input[i + 1] === '\n') i++
            row.push(field)
            field = ''
            rows.push(row)
            row = []
        } else {
            field += char
        }
    }
    // Flush the trailing field/row (file may not end with a newline).
    if (field.length > 0 || row.length > 0) {
        row.push(field)
        rows.push(row)
    }

    const nonEmptyRows = rows.filter((r) => r.some((c) => c.trim().length > 0))
    if (nonEmptyRows.length === 0) return []

    const headers = nonEmptyRows[0].map((h) => h.trim().toLowerCase())
    return nonEmptyRows.slice(1).map((cells) => {
        const obj: Record<string, string> = {}
        headers.forEach((h, idx) => {
            obj[h] = (cells[idx] ?? '').trim()
        })
        return obj
    })
}

// Validate parsed rows against the email/name requirements and flag duplicates
// within the same file. Returns the clean rows plus a list of human-readable
// issues that block those rows from being created.
const validateBulkRows = (
    parsed: Record<string, string>[]
): { rows: BulkRow[]; issues: string[] } => {
    const rows: BulkRow[] = []
    const issues: string[] = []
    const seenEmails = new Set<string>()

    parsed.forEach((r, idx) => {
        const lineNo = idx + 2 // +1 for header row, +1 for 1-based numbering
        const email = (r.email || '').trim()
        const name = (r.name || '').trim()
        const team = (r.team || '').trim()

        if (!email && !name) {
            issues.push(`Row ${lineNo}: missing email and name`)
            return
        }
        if (!name) {
            issues.push(`Row ${lineNo}: missing name (${email || 'no email'})`)
            return
        }
        if (!email) {
            issues.push(`Row ${lineNo}: missing email (${name})`)
            return
        }
        if (!EMAIL_REGEX.test(email)) {
            issues.push(`Row ${lineNo}: invalid email "${email}"`)
            return
        }
        const key = email.toLowerCase()
        if (seenEmails.has(key)) {
            issues.push(`Row ${lineNo}: duplicate email "${email}" in file`)
            return
        }
        seenEmails.add(key)
        rows.push({ email, name, team })
    })

    return { rows, issues }
}

// Build a downloadable results CSV and trigger a client-side download.
const downloadResultsCsv = (results: BulkResultRow[]) => {
    const escape = (v: string | undefined) => {
        const s = v ?? ''
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const header = ['email', 'name', 'team', 'status', 'password', 'reason']
    const lines = [header.join(',')]
    results.forEach((r) => {
        lines.push(
            [
                escape(r.email),
                escape(r.name),
                escape(r.team),
                escape(r.status),
                escape(r.password),
                escape(r.reason)
            ].join(',')
        )
    })
    const csv = lines.join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const stamp = new Date().toISOString().replace(/[:.]/g, '-')
    a.href = url
    a.download = `pcp-referral-users-${stamp}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
}

const PcpReferralUsersView = () => {
    const [activeTab, setActiveTab] = useState<TabKey>('users')
    const [users, setUsers] = useState<EngagementUser[]>([])
    const [teams, setTeams] = useState<PcpReferralTeam[]>([])
    const [userSearch, setUserSearch] = useState('')

    // Modal state. Single piece of state per modal type to keep this simple.
    const [createUserOpen, setCreateUserOpen] = useState(false)
    const [createdUserInfo, setCreatedUserInfo] = useState<{ email: string; password: string } | null>(null)
    const [bulkOpen, setBulkOpen] = useState(false)
    const [bulkResults, setBulkResults] = useState<BulkResultRow[] | null>(null)
    const [createTeamOpen, setCreateTeamOpen] = useState(false)
    const [editTeam, setEditTeam] = useState<PcpReferralTeam | null>(null)
    const [editUser, setEditUser] = useState<EngagementUser | null>(null)
    const [assignTeamFor, setAssignTeamFor] = useState<EngagementUser | null>(null)
    const [resetPasswordFor, setResetPasswordFor] = useState<EngagementUser | null>(null)

    useEffect(() => {
        loadAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadAll = async () => {
        Loading.circle('Loading users & teams…')
        try {
            await Promise.all([loadUsers(), loadTeams()])
        } finally {
            Loading.remove()
        }
    }

    const loadUsers = async () => {
        try {
            const res = await api({ url: '/pcp-referral-users-list' })
            if (res.data?.success) setUsers(res.data.users || [])
        } catch (err: any) {
            console.log(err)
            Report.failure('Error', err?.response?.data?.message || 'Failed to load users', 'Okay')
        }
    }

    const loadTeams = async () => {
        try {
            const res = await api({ url: '/pcp-referral-teams-list' })
            if (res.data?.success) setTeams(res.data.teams || [])
        } catch (err: any) {
            console.log(err)
            Report.failure('Error', err?.response?.data?.message || 'Failed to load teams', 'Okay')
        }
    }

    const filteredUsers = useMemo(() => {
        const q = userSearch.trim().toLowerCase()
        if (!q) return users
        return users.filter(
            (u) =>
                u.name?.toLowerCase().includes(q) ||
                u.email?.toLowerCase().includes(q) ||
                (u.team_name?.toLowerCase().includes(q) ?? false)
        )
    }, [users, userSearch])

    // ─── Users ─────────────────────────────────────────────────────────────

    const handleToggleUserStatus = (user: EngagementUser) => {
        const isActive = user.status === 'active'
        Confirm.show(
            isActive ? 'Disable User?' : 'Enable User?',
            `Are you sure you want to ${isActive ? 'disable' : 'enable'} ${user.email}?`,
            'Yes',
            'Cancel',
            async () => {
                Loading.circle('Updating user…')
                try {
                    const res = await api({
                        url: '/pcp-referral-users-update-status',
                        data: { email: user.email, enabled: !isActive }
                    })
                    if (res.data?.success) {
                        Notify.success(res.data.message || 'User updated')
                        await loadUsers()
                    } else {
                        Report.failure('Error', res.data?.message || 'Failed to update', 'Okay')
                    }
                } catch (err: any) {
                    Report.failure('Error', err?.response?.data?.message || 'Failed to update', 'Okay')
                } finally {
                    Loading.remove()
                }
            }
        )
    }

    const submitCreateUser = async (payload: { email: string; name: string; team_id: number | null }) => {
        const temporaryPassword = generateTempPassword()
        Loading.circle('Creating user…')
        try {
            const res = await api({
                url: '/pcp-referral-users-create',
                data: {
                    email: payload.email,
                    name: payload.name,
                    team_id: payload.team_id,
                    temporaryPassword
                }
            })
            if (res.data?.success) {
                setCreatedUserInfo({ email: payload.email, password: temporaryPassword })
                setCreateUserOpen(false)
                await Promise.all([loadUsers(), loadTeams()])
            } else {
                Report.failure('Error', res.data?.message || res.data?.error || 'Failed to create user', 'Okay')
            }
        } catch (err: any) {
            Report.failure('Error', err?.response?.data?.message || 'Failed to create user', 'Okay')
        } finally {
            Loading.remove()
        }
    }

    // Create every valid row one-by-one, reusing the same endpoint as the single
    // create flow so behaviour (Cognito user + temp password email) is identical.
    const runBulkCreate = async (rows: BulkRow[]) => {
        // Resolve team names against the loaded teams once, up front.
        const teamByName = new Map(teams.map((t) => [t.name.trim().toLowerCase(), t]))
        const results: BulkResultRow[] = []

        // Show the overlay once, then update its text per-row. notiflix ignores a
        // fresh Loading.circle() call while already visible, so the counter must
        // be advanced with Loading.change() or it appears stuck on the first row.
        Loading.circle(`Creating user 1 of ${rows.length}…`)

        for (let i = 0; i < rows.length; i++) {
            const { email, name, team } = rows[i]
            Loading.change(`Creating user ${i + 1} of ${rows.length}…`)

            let team_id: number | null = null
            if (team) {
                const match = teamByName.get(team.toLowerCase())
                if (!match) {
                    results.push({ email, name, team, status: 'failed', reason: `Unknown team: ${team}` })
                    continue
                }
                team_id = match.id
            }

            const temporaryPassword = generateTempPassword()
            try {
                const res = await api({
                    url: '/pcp-referral-users-create',
                    data: { email, name, team_id, temporaryPassword }
                })
                if (res.data?.success) {
                    results.push({ email, name, team, status: 'created', password: temporaryPassword })
                } else {
                    results.push({
                        email,
                        name,
                        team,
                        status: 'failed',
                        reason: res.data?.message || res.data?.error || 'Failed to create user'
                    })
                }
            } catch (err: any) {
                results.push({
                    email,
                    name,
                    team,
                    status: 'failed',
                    reason: err?.response?.data?.message || err?.response?.data?.error || 'Request failed'
                })
            }
        }

        Loading.remove()
        await Promise.all([loadUsers(), loadTeams()])
        setBulkOpen(false)
        setBulkResults(results)
    }

    const submitEditUser = async (payload: { email: string; name: string }) => {
        Loading.circle('Updating user…')
        try {
            const res = await api({ url: '/pcp-referral-users-update', data: payload })
            if (res.data?.success) {
                Notify.success(res.data.message || 'User updated')
                setEditUser(null)
                await loadUsers()
            } else {
                Report.failure('Error', res.data?.message || 'Failed to update', 'Okay')
            }
        } catch (err: any) {
            Report.failure('Error', err?.response?.data?.message || 'Failed to update', 'Okay')
        } finally {
            Loading.remove()
        }
    }

    const submitAssignTeam = async (email: string, team_id: number | null) => {
        Loading.circle('Assigning team…')
        try {
            const res = await api({ url: '/pcp-referral-users-assign-team', data: { email, team_id } })
            if (res.data?.success) {
                Notify.success('Team assignment updated')
                setAssignTeamFor(null)
                await Promise.all([loadUsers(), loadTeams()])
            } else {
                Report.failure('Error', res.data?.message || 'Failed to assign team', 'Okay')
            }
        } catch (err: any) {
            Report.failure('Error', err?.response?.data?.message || 'Failed to assign team', 'Okay')
        } finally {
            Loading.remove()
        }
    }

    const handleToggleTeamLeader = (user: EngagementUser) => {
        if (!user.team_id) {
            Report.info('Assign a Team First', 'A user must belong to a team before becoming a team leader.', 'Okay')
            return
        }
        const makeLeader = !user.is_leader
        Confirm.show(
            makeLeader ? 'Make Team Leader?' : 'Remove Team Leader?',
            makeLeader
                ? `Make ${user.name} a leader of "${user.team_name}"? They'll be able to view their team's stats.`
                : `Remove team leader status from ${user.name}?`,
            'Yes',
            'Cancel',
            async () => {
                Loading.circle('Updating team leader…')
                try {
                    const res = await api({
                        url: '/pcp-referral-users-set-team-leader',
                        data: { email: user.email, is_leader: makeLeader },
                    })
                    if (res.data?.success) {
                        Notify.success(res.data.message || 'Team leader updated')
                        await loadUsers()
                    } else {
                        Report.failure('Error', res.data?.message || 'Failed to update', 'Okay')
                    }
                } catch (err: any) {
                    Report.failure('Error', err?.response?.data?.message || 'Failed to update', 'Okay')
                } finally {
                    Loading.remove()
                }
            }
        )
    }

    const submitResetPassword = async (email: string, newPassword: string) => {
        Loading.circle('Resetting password…')
        try {
            const res = await api({
                url: '/pcp-referral-users-reset-password',
                data: { email, newPassword }
            })
            if (res.data?.success) {
                Report.success(
                    'Password Reset',
                    `New password for ${email}:\n\n${newPassword}\n\nShare this securely with the user.`,
                    'Okay'
                )
                setResetPasswordFor(null)
            } else {
                Report.failure('Error', res.data?.message || 'Failed to reset', 'Okay')
            }
        } catch (err: any) {
            Report.failure('Error', err?.response?.data?.message || 'Failed to reset', 'Okay')
        } finally {
            Loading.remove()
        }
    }

    // ─── Teams ─────────────────────────────────────────────────────────────

    const submitCreateTeam = async (payload: { name: string; description: string }) => {
        Loading.circle('Creating team…')
        try {
            const res = await api({ url: '/pcp-referral-teams-create', data: payload })
            if (res.data?.success) {
                Notify.success('Team created')
                setCreateTeamOpen(false)
                await loadTeams()
            } else {
                Report.failure('Error', res.data?.message || 'Failed to create team', 'Okay')
            }
        } catch (err: any) {
            Report.failure('Error', err?.response?.data?.message || 'Failed to create team', 'Okay')
        } finally {
            Loading.remove()
        }
    }

    const submitEditTeam = async (payload: { id: number; name: string; description: string }) => {
        Loading.circle('Updating team…')
        try {
            const res = await api({ url: '/pcp-referral-teams-update', data: payload })
            if (res.data?.success) {
                Notify.success('Team updated')
                setEditTeam(null)
                await loadTeams()
            } else {
                Report.failure('Error', res.data?.message || 'Failed to update team', 'Okay')
            }
        } catch (err: any) {
            Report.failure('Error', err?.response?.data?.message || 'Failed to update team', 'Okay')
        } finally {
            Loading.remove()
        }
    }

    const handleDeleteTeam = (team: PcpReferralTeam) => {
        Confirm.show(
            'Delete Team?',
            `Delete "${team.name}"? ${team.member_count || 0} member(s) will be unassigned but their user accounts will be kept.`,
            'Delete',
            'Cancel',
            async () => {
                Loading.circle('Deleting team…')
                try {
                    const res = await api({ url: '/pcp-referral-teams-delete', data: { id: team.id } })
                    if (res.data?.success) {
                        Notify.success('Team deleted')
                        await Promise.all([loadTeams(), loadUsers()])
                    } else {
                        Report.failure('Error', res.data?.message || 'Failed to delete', 'Okay')
                    }
                } catch (err: any) {
                    Report.failure('Error', err?.response?.data?.message || 'Failed to delete', 'Okay')
                } finally {
                    Loading.remove()
                }
            }
        )
    }

    return (
        <div className={styles.container}>
            <div className={styles.tabs}>
                <button
                    className={`${styles.tabButton} ${activeTab === 'users' ? styles.tabButtonActive : ''}`}
                    onClick={() => setActiveTab('users')}
                >
                    Users ({users.length})
                </button>
                <button
                    className={`${styles.tabButton} ${activeTab === 'teams' ? styles.tabButtonActive : ''}`}
                    onClick={() => setActiveTab('teams')}
                >
                    Teams ({teams.length})
                </button>
            </div>

            {activeTab === 'users' && (
                <>
                    <div className={styles.toolbar}>
                        <h3>Engagement Users</h3>
                        <div className={styles.toolbarActions}>
                            <input
                                className={styles.searchInput}
                                placeholder="Search by name, email, team…"
                                value={userSearch}
                                onChange={(e) => setUserSearch(e.target.value)}
                            />
                            <button className={styles.secondaryButton} onClick={() => setBulkOpen(true)}>
                                Bulk Upload (CSV)
                            </button>
                            <button className={styles.primaryButton} onClick={() => setCreateUserOpen(true)}>
                                + New User
                            </button>
                        </div>
                    </div>

                    {filteredUsers.length === 0 ? (
                        <div className={styles.emptyState}>
                            {users.length === 0
                                ? 'No Engagement users yet. Click "+ New User" to create one.'
                                : 'No users match your search.'}
                        </div>
                    ) : (
                        <div className={styles.tableScroll}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Team</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map((u) => (
                                        <tr key={u.id}>
                                            <td>{u.name}</td>
                                            <td>{u.email}</td>
                                            <td>
                                                {u.team_name ? (
                                                    <span className={styles.teamPill}>{u.team_name}</span>
                                                ) : (
                                                    <span className={styles.teamPillEmpty}>Unassigned</span>
                                                )}
                                                {u.is_leader && (
                                                    <span className={styles.leaderBadge} title="Team leader">
                                                        Leader
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <StatusPill status={u.status} />
                                            </td>
                                            <td>
                                                <div className={styles.rowActions}>
                                                    <button
                                                        className={styles.subtleButton}
                                                        onClick={() => setEditUser(u)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className={styles.subtleButton}
                                                        onClick={() => setAssignTeamFor(u)}
                                                    >
                                                        Team
                                                    </button>
                                                    <button
                                                        className={styles.subtleButton}
                                                        onClick={() => handleToggleTeamLeader(u)}
                                                        disabled={!u.team_id}
                                                        title={
                                                            !u.team_id
                                                                ? 'Assign a team first'
                                                                : u.is_leader
                                                                ? 'Remove team leader status'
                                                                : 'Make team leader'
                                                        }
                                                    >
                                                        {u.is_leader ? 'Remove Leader' : 'Make Leader'}
                                                    </button>
                                                    <button
                                                        className={styles.subtleButton}
                                                        onClick={() => setResetPasswordFor(u)}
                                                    >
                                                        Reset PW
                                                    </button>
                                                    <button
                                                        className={styles.subtleButton}
                                                        onClick={() => handleToggleUserStatus(u)}
                                                    >
                                                        {u.status === 'active' ? 'Disable' : 'Enable'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {activeTab === 'teams' && (
                <>
                    <div className={styles.toolbar}>
                        <h3>Teams</h3>
                        <div className={styles.toolbarActions}>
                            <button className={styles.primaryButton} onClick={() => setCreateTeamOpen(true)}>
                                + New Team
                            </button>
                        </div>
                    </div>

                    {teams.length === 0 ? (
                        <div className={styles.emptyState}>
                            No teams yet. Click "+ New Team" to create one.
                        </div>
                    ) : (
                        <div className={styles.tableScroll}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Description</th>
                                        <th>Members</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {teams.map((t) => (
                                        <tr key={t.id}>
                                            <td>{t.name}</td>
                                            <td>{t.description || <span className={styles.teamPillEmpty}>—</span>}</td>
                                            <td>{t.member_count ?? 0}</td>
                                            <td>
                                                <div className={styles.rowActions}>
                                                    <button
                                                        className={styles.subtleButton}
                                                        onClick={() => setEditTeam(t)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className={styles.dangerButton}
                                                        onClick={() => handleDeleteTeam(t)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {createUserOpen && (
                <CreateUserModal
                    teams={teams}
                    onCancel={() => setCreateUserOpen(false)}
                    onSubmit={submitCreateUser}
                />
            )}

            {createdUserInfo && (
                <TempPasswordModal
                    info={createdUserInfo}
                    onClose={() => setCreatedUserInfo(null)}
                />
            )}

            {bulkOpen && (
                <BulkUploadModal
                    teams={teams}
                    onCancel={() => setBulkOpen(false)}
                    onConfirm={runBulkCreate}
                />
            )}

            {bulkResults && (
                <BulkResultsModal results={bulkResults} onClose={() => setBulkResults(null)} />
            )}

            {editUser && (
                <EditUserModal user={editUser} onCancel={() => setEditUser(null)} onSubmit={submitEditUser} />
            )}

            {assignTeamFor && (
                <AssignTeamModal
                    user={assignTeamFor}
                    teams={teams}
                    onCancel={() => setAssignTeamFor(null)}
                    onSubmit={(team_id) => submitAssignTeam(assignTeamFor.email, team_id)}
                />
            )}

            {resetPasswordFor && (
                <ResetPasswordModal
                    user={resetPasswordFor}
                    onCancel={() => setResetPasswordFor(null)}
                    onSubmit={(pwd) => submitResetPassword(resetPasswordFor.email, pwd)}
                />
            )}

            {createTeamOpen && (
                <CreateOrEditTeamModal
                    title="New Team"
                    onCancel={() => setCreateTeamOpen(false)}
                    onSubmit={(p) => submitCreateTeam(p)}
                />
            )}

            {editTeam && (
                <CreateOrEditTeamModal
                    title="Edit Team"
                    initial={editTeam}
                    onCancel={() => setEditTeam(null)}
                    onSubmit={(p) => submitEditTeam({ ...p, id: editTeam.id })}
                />
            )}
        </div>
    )
}

// ─── Sub-components ────────────────────────────────────────────────────────

const StatusPill = ({ status }: { status: string }) => {
    if (status === 'active') return <span className={`${styles.statusPill} ${styles.statusActive}`}>Active</span>
    if (status === 'disabled')
        return <span className={`${styles.statusPill} ${styles.statusDisabled}`}>Disabled</span>
    return <span className={`${styles.statusPill} ${styles.statusPending}`}>{status || 'Unknown'}</span>
}

const Modal = ({
    title,
    onClose,
    children,
    footer,
}: {
    title: string
    onClose: () => void
    children: React.ReactNode
    footer?: React.ReactNode
}) => (
    <div className={styles.modalBackdrop} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
                <h3>{title}</h3>
                <button className={styles.subtleButton} onClick={onClose}>
                    ✕
                </button>
            </div>
            <div className={styles.modalBody}>{children}</div>
            {footer && <div className={styles.modalFooter}>{footer}</div>}
        </div>
    </div>
)

const CreateUserModal = ({
    teams,
    onCancel,
    onSubmit,
}: {
    teams: PcpReferralTeam[]
    onCancel: () => void
    onSubmit: (payload: { email: string; name: string; team_id: number | null }) => void
}) => {
    const [email, setEmail] = useState('')
    const [name, setName] = useState('')
    const [teamId, setTeamId] = useState<string>('')
    const [error, setError] = useState('')

    const handleSubmit = () => {
        const e = email.trim()
        const n = name.trim()
        if (!n) return setError('Name is required')
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return setError('Valid email is required')
        setError('')
        onSubmit({ email: e, name: n, team_id: teamId ? Number(teamId) : null })
    }

    return (
        <Modal
            title="New Engagement User"
            onClose={onCancel}
            footer={
                <>
                    <button className={styles.subtleButton} onClick={onCancel}>
                        Cancel
                    </button>
                    <button className={styles.primaryButton} onClick={handleSubmit}>
                        Create User
                    </button>
                </>
            }
        >
            <div className={styles.helperBox}>
                A temporary password will be generated for you to share with the new user. They&apos;ll be required to
                change it on first sign-in.
            </div>
            {error && <p className={styles.errorText}>{error}</p>}
            <div className={styles.formGroup}>
                <label>Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
            </div>
            <div className={styles.formGroup}>
                <label>Email</label>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="user@example.com"
                />
            </div>
            <div className={styles.formGroup}>
                <label>Team (optional)</label>
                <select value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                    <option value="">— Unassigned —</option>
                    {teams.map((t) => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </select>
                <small>You can change this later from the user&apos;s row.</small>
            </div>
        </Modal>
    )
}

const TempPasswordModal = ({
    info,
    onClose,
}: {
    info: { email: string; password: string }
    onClose: () => void
}) => (
    <Modal
        title="User Created"
        onClose={onClose}
        footer={
            <button className={styles.primaryButton} onClick={onClose}>
                Done
            </button>
        }
    >
        <div className={styles.tempPasswordBox}>
            <h4>{info.email}</h4>
            <p style={{ margin: '0 0 0.5rem' }}>Temporary password:</p>
            <code>{info.password}</code>
        </div>
        <p style={{ color: '#5d6d7e', fontSize: '0.85rem', margin: 0 }}>
            Cognito has also emailed this password to the user. They&apos;ll be prompted to change it on first
            sign-in.
        </p>
    </Modal>
)

const BulkUploadModal = ({
    teams,
    onCancel,
    onConfirm,
}: {
    teams: PcpReferralTeam[]
    onCancel: () => void
    onConfirm: (rows: BulkRow[]) => void
}) => {
    const [fileName, setFileName] = useState('')
    const [rows, setRows] = useState<BulkRow[]>([])
    const [issues, setIssues] = useState<string[]>([])
    const [error, setError] = useState('')

    const handleFile = (file: File | undefined) => {
        setError('')
        setRows([])
        setIssues([])
        setFileName('')
        if (!file) return
        setFileName(file.name)
        const reader = new FileReader()
        reader.onload = () => {
            try {
                const text = String(reader.result || '')
                const parsed = parseCsv(text)
                if (parsed.length === 0) {
                    setError('No data rows found. Ensure the file has a header row (email, name) plus at least one row.')
                    return
                }
                if (!('email' in parsed[0]) || !('name' in parsed[0])) {
                    setError('CSV must include "email" and "name" columns in the header row.')
                    return
                }
                const { rows: valid, issues: found } = validateBulkRows(parsed)
                setRows(valid)
                setIssues(found)
                if (valid.length === 0) {
                    setError('No valid rows to create. Fix the issues listed below and re-upload.')
                }
            } catch (e: any) {
                setError('Could not read the CSV file. Please check the format and try again.')
            }
        }
        reader.onerror = () => setError('Could not read the file.')
        reader.readAsText(file)
    }

    return (
        <Modal
            title="Bulk Upload Users"
            onClose={onCancel}
            footer={
                <>
                    <button className={styles.subtleButton} onClick={onCancel}>
                        Cancel
                    </button>
                    <button
                        className={styles.primaryButton}
                        onClick={() => onConfirm(rows)}
                        disabled={rows.length === 0}
                    >
                        Create {rows.length || ''} User{rows.length === 1 ? '' : 's'}
                    </button>
                </>
            }
        >
            <div className={styles.helperBox}>
                Upload a CSV with columns <strong>email</strong>, <strong>name</strong> and an optional{' '}
                <strong>team</strong> (matched by team name). Each user gets a temporary password they must change on
                first sign-in. After creation you can download a CSV of all credentials and failures.
            </div>

            <div className={styles.formGroup}>
                <label>CSV File</label>
                <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                />
                {teams.length > 0 && (
                    <small>
                        Available teams: {teams.map((t) => t.name).join(', ')}
                    </small>
                )}
            </div>

            {error && <p className={styles.errorText}>{error}</p>}

            {fileName && rows.length > 0 && (
                <div className={styles.tempPasswordBox}>
                    <h4>{fileName}</h4>
                    <p style={{ margin: 0 }}>
                        {rows.length} valid row{rows.length === 1 ? '' : 's'} ready to create
                        {issues.length > 0 ? `, ${issues.length} row(s) will be skipped` : ''}.
                    </p>
                </div>
            )}

            {issues.length > 0 && (
                <div className={styles.bulkIssues}>
                    <strong>Skipped rows:</strong>
                    <ul>
                        {issues.map((iss, i) => (
                            <li key={i}>{iss}</li>
                        ))}
                    </ul>
                </div>
            )}
        </Modal>
    )
}

const BulkResultsModal = ({
    results,
    onClose,
}: {
    results: BulkResultRow[]
    onClose: () => void
}) => {
    const created = results.filter((r) => r.status === 'created').length
    const failed = results.length - created

    return (
        <Modal
            title="Bulk Upload Results"
            onClose={onClose}
            footer={
                <>
                    <button className={styles.subtleButton} onClick={onClose}>
                        Done
                    </button>
                    <button className={styles.primaryButton} onClick={() => downloadResultsCsv(results)}>
                        Download Results CSV
                    </button>
                </>
            }
        >
            <div className={styles.bulkSummary}>
                <span className={`${styles.statusPill} ${styles.statusActive}`}>{created} created</span>
                <span className={`${styles.statusPill} ${styles.statusDisabled}`}>{failed} failed</span>
            </div>
            <p style={{ color: '#5d6d7e', fontSize: '0.85rem', margin: '0 0 1rem' }}>
                Download the results CSV to share the temporary passwords securely. Cognito has also emailed each
                created user their password.
            </p>
            <div className={styles.bulkResultsTableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Email</th>
                            <th>Name</th>
                            <th>Status</th>
                            <th>Password / Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {results.map((r, i) => (
                            <tr key={i}>
                                <td>{r.email}</td>
                                <td>{r.name}</td>
                                <td>
                                    {r.status === 'created' ? (
                                        <span className={`${styles.statusPill} ${styles.statusActive}`}>Created</span>
                                    ) : (
                                        <span className={`${styles.statusPill} ${styles.statusDisabled}`}>Failed</span>
                                    )}
                                </td>
                                <td>
                                    {r.status === 'created' ? (
                                        <code className={styles.inlinePassword}>{r.password}</code>
                                    ) : (
                                        <span className={styles.errorText} style={{ margin: 0 }}>
                                            {r.reason}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Modal>
    )
}

const EditUserModal = ({
    user,
    onCancel,
    onSubmit,
}: {
    user: EngagementUser
    onCancel: () => void
    onSubmit: (payload: { email: string; name: string }) => void
}) => {
    const [name, setName] = useState(user.name)
    const [error, setError] = useState('')

    const handleSubmit = () => {
        if (!name.trim()) return setError('Name is required')
        setError('')
        onSubmit({ email: user.email, name: name.trim() })
    }

    return (
        <Modal
            title={`Edit ${user.email}`}
            onClose={onCancel}
            footer={
                <>
                    <button className={styles.subtleButton} onClick={onCancel}>
                        Cancel
                    </button>
                    <button className={styles.primaryButton} onClick={handleSubmit}>
                        Save
                    </button>
                </>
            }
        >
            {error && <p className={styles.errorText}>{error}</p>}
            <div className={styles.formGroup}>
                <label>Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className={styles.formGroup}>
                <label>Email</label>
                <input value={user.email} readOnly />
                <small>Email cannot be changed — create a new account if required.</small>
            </div>
        </Modal>
    )
}

const AssignTeamModal = ({
    user,
    teams,
    onCancel,
    onSubmit,
}: {
    user: EngagementUser
    teams: PcpReferralTeam[]
    onCancel: () => void
    onSubmit: (team_id: number | null) => void
}) => {
    const [teamId, setTeamId] = useState<string>(user.team_id ? String(user.team_id) : '')

    return (
        <Modal
            title={`Assign Team — ${user.name}`}
            onClose={onCancel}
            footer={
                <>
                    <button className={styles.subtleButton} onClick={onCancel}>
                        Cancel
                    </button>
                    <button
                        className={styles.primaryButton}
                        onClick={() => onSubmit(teamId ? Number(teamId) : null)}
                    >
                        Save
                    </button>
                </>
            }
        >
            <div className={styles.formGroup}>
                <label>Team</label>
                <select value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                    <option value="">— Unassigned —</option>
                    {teams.map((t) => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </select>
                <small>Each user belongs to a single team. Saving will replace any existing assignment.</small>
            </div>
        </Modal>
    )
}

const ResetPasswordModal = ({
    user,
    onCancel,
    onSubmit,
}: {
    user: EngagementUser
    onCancel: () => void
    onSubmit: (newPassword: string) => void
}) => {
    const [password, setPassword] = useState(generateTempPassword())

    return (
        <Modal
            title={`Reset Password — ${user.email}`}
            onClose={onCancel}
            footer={
                <>
                    <button className={styles.subtleButton} onClick={onCancel}>
                        Cancel
                    </button>
                    <button className={styles.primaryButton} onClick={() => onSubmit(password)}>
                        Reset Password
                    </button>
                </>
            }
        >
            <div className={styles.helperBox}>
                This will set a new permanent password for the user. They&apos;ll be able to sign in immediately with
                it.
            </div>
            <div className={styles.formGroup}>
                <label>New Password</label>
                <input value={password} onChange={(e) => setPassword(e.target.value)} />
                <small>
                    Must satisfy your Cognito password policy (min 8 chars, mixed case, number, symbol).{' '}
                    <button
                        type="button"
                        className={styles.subtleButton}
                        style={{ marginLeft: '0.25rem' }}
                        onClick={() => setPassword(generateTempPassword())}
                    >
                        Regenerate
                    </button>
                </small>
            </div>
        </Modal>
    )
}

const CreateOrEditTeamModal = ({
    title,
    initial,
    onCancel,
    onSubmit,
}: {
    title: string
    initial?: PcpReferralTeam
    onCancel: () => void
    onSubmit: (payload: { name: string; description: string }) => void
}) => {
    const [name, setName] = useState(initial?.name || '')
    const [description, setDescription] = useState(initial?.description || '')
    const [error, setError] = useState('')

    const handleSubmit = () => {
        if (!name.trim()) return setError('Name is required')
        setError('')
        onSubmit({ name: name.trim(), description: description.trim() })
    }

    return (
        <Modal
            title={title}
            onClose={onCancel}
            footer={
                <>
                    <button className={styles.subtleButton} onClick={onCancel}>
                        Cancel
                    </button>
                    <button className={styles.primaryButton} onClick={handleSubmit}>
                        Save
                    </button>
                </>
            }
        >
            {error && <p className={styles.errorText}>{error}</p>}
            <div className={styles.formGroup}>
                <label>Team Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Alpha Team" />
            </div>
            <div className={styles.formGroup}>
                <label>Description (optional)</label>
                <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Short note for context"
                />
            </div>
        </Modal>
    )
}

export default PcpReferralUsersView
